// SCR-STAFF-925 — ممیزی و رویدادهای امنیتی (AP-IAM-03؛ US-IAM-064): رویدادهای امنیتی اعضای مرکز (ورود، ورود ناموفق، قفل، رمز،
// 2FA، دستگاه تازه، خروج اجباری) با فیلتر نوع، کارمند و بازه (iam.security_events.view)؛ IP تقریبی (پنهان‌سازی سرور —
// C-AGENT-388). زبانه «ممیزی عملیات» (US-IAM-062: فیلترهای کامل و خروجی CSV) از AP-IAM-04 (AuditEventsTable).
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { unwrap, type SecurityEventView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, JalaliDate, JalaliDatePickerRHF, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { AuditEventsTab } from './AuditEventsTable';
import { iamKeys } from './common';

export const SCR_STAFF_925 = 'SCR-STAFF-925';

export const SECURITY_EVENT_TYPES = [
  'login_succeeded',
  'login_failed',
  'account_locked',
  'account_locked_permanently',
  'logout',
  'password_changed',
  'mfa_configured',
  'mfa_removed',
  'mfa_reset',
  'recovery_codes_generated',
  'recovery_code_used',
  'new_device_login',
  'session_revoked',
  'sessions_revoked_by_admin',
] as const;

const PAGE_SIZE = 50;
/** آغاز روز کسب‌وکاری تهران (بدون DST از ۱۴۰۱) برای فیلتر بازه. */
const TEHRAN_OFFSET = '+03:30';

function dayStart(date: string): string {
  return `${date}T00:00:00${TEHRAN_OFFSET}`;
}

function nextDayStart(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1));
  return dayStart(next.toISOString().slice(0, 10));
}

interface RangeForm {
  from: string | null;
  to: string | null;
}

function SecurityEventsTable() {
  const { t } = useTranslation('iam');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const canListMembers = access.can('iam.member.view');
  const [type, setType] = useState('');
  const [membershipId, setMembershipId] = useState('');
  const { control } = useForm<RangeForm>({ defaultValues: { from: null, to: null } });
  const from = useWatch({ control, name: 'from' });
  const to = useWatch({ control, name: 'to' });

  const members = useQuery({
    queryKey: iamKeys.memberships(session?.tenantId, { picker: true }),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/identity/memberships', { params: { query: { limit: 200 } } })),
    enabled: canListMembers,
    staleTime: 60_000,
  });

  const filters = useMemo(
    () => ({
      type: type || undefined,
      membershipId: membershipId || undefined,
      from: from ? dayStart(from) : undefined,
      to: to ? nextDayStart(to) : undefined,
    }),
    [type, membershipId, from, to],
  );

  const events = useInfiniteQuery({
    queryKey: iamKeys.securityEvents(session?.tenantId, filters),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/identity/security-events', {
          params: { query: { ...filters, cursor: pageParam, limit: PAGE_SIZE } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const columns: DataTableColumn<SecurityEventView>[] = [
    {
      id: 'time',
      headerKey: 'iam:security_events.columns.time',
      cell: (row) => <JalaliDate value={row.occurredAt} withTime />,
    },
    {
      id: 'type',
      headerKey: 'iam:security_events.columns.type',
      cell: (row) => (
        <span data-testid={`security-event-${row.eventId}`} data-type={row.type}>
          {t(`iam:security_events.types.${row.type}`, { defaultValue: row.type })}
        </span>
      ),
    },
    {
      id: 'member',
      headerKey: 'iam:security_events.columns.member',
      cell: (row) => row.userName ?? t('common:states.none'),
    },
    {
      id: 'ip',
      headerKey: 'iam:security_events.columns.ip',
      cell: (row) => (row.ipAddress ? <bdi dir="ltr">{row.ipAddress}</bdi> : t('common:states.none')),
    },
    {
      id: 'client',
      headerKey: 'iam:security_events.columns.client',
      cell: (row) => (row.client ? <bdi dir="ltr">{row.client}</bdi> : t('common:states.none')),
    },
    {
      id: 'scope',
      headerKey: 'iam:security_events.columns.scope',
      cell: (row) => t(`iam:security_events.scope.${row.scope}`, { defaultValue: row.scope }),
    },
  ];

  return (
    <DataTable
      captionKey="iam:security_events.title"
      columns={columns}
      rows={events.data?.pages.flatMap((page) => page.items)}
      getRowId={(row) => row.eventId}
      isLoading={events.isPending}
      error={events.error}
      onRetry={() => void events.refetch()}
      hasMore={events.hasNextPage}
      onLoadMore={() => void events.fetchNextPage()}
      isFetchingMore={events.isFetchingNextPage}
      emptyTitleKey="iam:security_events.empty"
      toolbar={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            select
            label={t('iam:security_events.type')}
            value={type}
            onChange={(event) => setType(event.target.value)}
            sx={{ minWidth: 200 }}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          >
            <MenuItem value="">{t('iam:security_events.all_types')}</MenuItem>
            {SECURITY_EVENT_TYPES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`iam:security_events.types.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          {canListMembers && (
            <TextField
              select
              label={t('iam:security_events.member')}
              value={membershipId}
              onChange={(event) => setMembershipId(event.target.value)}
              sx={{ minWidth: 200 }}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
            >
              <MenuItem value="">{t('iam:security_events.all_members')}</MenuItem>
              {(members.data?.items ?? []).map((member) => (
                <MenuItem key={member.membershipId} value={member.membershipId}>
                  {member.displayName}
                </MenuItem>
              ))}
            </TextField>
          )}
          <JalaliDatePickerRHF control={control} name="from" labelKey="iam:security_events.from" />
          <JalaliDatePickerRHF control={control} name="to" labelKey="iam:security_events.to" />
        </Stack>
      }
    />
  );
}

type AuditTab = 'security' | 'audit';

export function SecurityEventsPage() {
  const { t } = useTranslation('iam');
  const access = useAccess();
  const canSecurity = access.can('iam.security_events.view');
  const canAudit = access.can('iam.audit.view');
  const [selected, setSelected] = useState<AuditTab>('security');
  const tab: AuditTab = canSecurity ? selected : 'audit';
  return (
    <ListPage screenId={SCR_STAFF_925} titleKey="iam:security_events.title" subtitleKey="iam:security_events.subtitle">
      <Tabs value={tab} onChange={(_, value: AuditTab) => setSelected(value)} sx={{ mb: 2 }}>
        {canSecurity && <Tab value="security" label={t('iam:security_events.tabs.security')} />}
        {canAudit && <Tab value="audit" label={t('iam:security_events.tabs.audit')} />}
      </Tabs>
      {tab === 'security' ? <SecurityEventsTable /> : <AuditEventsTab />}
    </ListPage>
  );
}
